import { useFeatureGate } from '@statsig/expo-bindings';
import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useState } from 'react';
import { TouchableWithoutFeedback } from 'react-native';
import {
    BodyMediumEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    Icon,
    LabelEmphasized,
    SettingsIcon,
    Sheet,
    tokens,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { FeatureFlags } from '../../feature-flags/FeatureFlags';
import { captureException } from '../../logger/Logger';
import useGetBillDiscoveryReview from '../hooks/useGetBillDiscoveryReview';
import { activeBillsAtom } from '../state/BillsAtoms';
import { useClearAllBills } from './adminHooks';

export const OpenBillsAdminSheetButton = () => {
    const { value: enableInternalBillsAdmin } = useFeatureGate(
        FeatureFlags.ENABLE_INTERNAL_BILLS_ADMIN,
    );

    const [isInternalBillsAdminOpen, setIsInternalBillsAdminOpen] =
        useState(false);

    const onPress = () => {
        setIsInternalBillsAdminOpen(true);
    };

    if (!enableInternalBillsAdmin) {
        return null;
    }

    return (
        <>
            <TouchableWithoutFeedback onPress={onPress}>
                <View
                    borderRadius={100}
                    backgroundColor={tokens.color.light.red.red200}
                    width={40}
                    height={40}
                    alignItems="center"
                    justifyContent="center"
                    position="absolute"
                    top={60}
                    right={10}
                >
                    <Icon icon={SettingsIcon} color="$black" />
                </View>
            </TouchableWithoutFeedback>
            <BillsAdminSheet
                isOpen={isInternalBillsAdminOpen}
                onClose={() => {
                    setIsInternalBillsAdminOpen(false);
                }}
            />
        </>
    );
};

const BillsAdminSheet = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => {
    const user = useAuthedUser();
    const { mutate: clearAllBills } = useClearAllBills();
    const storeActiveBills = useSetAtom(activeBillsAtom);
    const client = useQueryClient();
    const [clearedBills, setClearedBills] = useState(false);

    const handleClose = () => {
        setClearedBills(false);
        onClose();
    };

    const { refetch } = useGetBillDiscoveryReview(user.membership?.id ?? '');

    return (
        <Sheet isOpen={isOpen} onClose={handleClose} showCloseButton={false}>
            <YStack alignItems="center" gap={8}>
                <YStack alignItems="center" gap={1}>
                    <LabelEmphasized>(Internal Only)</LabelEmphasized>
                    <BodyMediumEmphasized>
                        Manage membership bills data
                    </BodyMediumEmphasized>
                </YStack>

                <XStack gap={10} alignItems="center" justifyContent="center">
                    {clearedBills ? (
                        <LabelEmphasized>Stored bills cleared</LabelEmphasized>
                    ) : (
                        <Button
                            buttonProps={{ width: 180 }}
                            size={ButtonSize.SMALL}
                            variant={ButtonVariant.SECONDARY}
                            tone="danger"
                            text="Clear my stored bills"
                            onPress={() => {
                                clearAllBills(
                                    {
                                        urlParams: {
                                            membershipId:
                                                user?.membership?.id ?? '',
                                        },
                                    },
                                    {
                                        onSuccess: () => {
                                            setClearedBills(true);
                                            storeActiveBills([]);
                                            void client.invalidateQueries({
                                                queryKey: [
                                                    `useGetBillDiscoveryReview/${user?.membership?.id ?? ''}`,
                                                    'getPendingJobs',
                                                    `getBills/${user?.membership?.id ?? ''}/ACTIVE`,
                                                ],
                                            });
                                            void refetch();
                                            router.navigate('/home');
                                        },
                                        onError: (error) => {
                                            captureException(error);
                                        },
                                    },
                                );
                            }}
                        />
                    )}
                </XStack>
            </YStack>
        </Sheet>
    );
};
